import type { ArtefactOutline, Folder } from './api'

/**
 * The artefact explorer's folder tree — assembly + the drag rules the panel needs locally.
 *
 * Folders are a navigation grouping. They never touch a room key, the CRDT or the export: moving
 * an artefact between folders changes where it is listed and nothing else. The backend owns the
 * rules (backend/domain/folders.ts) and re-checks every write; what lives here is the part the UI
 * cannot ask the server about mid-drag — whether a drop is worth offering at all.
 *
 * Guiding principle for the assembly below: an artefact NEVER disappears. A folder that is
 * unknown, or whose parent chain loops, is rendered at the root rather than dropped — otherwise a
 * teammate deleting a folder in another tab would make content vanish from this one.
 */

/** Maximum folder nesting; mirrors MAX_FOLDER_DEPTH in the backend domain. */
export const MAX_FOLDER_DEPTH = 5

export interface FolderNode {
  folder: Folder
  /** 1 for a root-level folder. */
  depth: number
  children: FolderNode[]
  /** The artefacts sitting directly in this folder, in their persisted order. */
  artefacts: ArtefactOutline[]
}

export interface ExplorerTree {
  roots: FolderNode[]
  /** Artefacts at the root level — outside any folder. */
  artefacts: ArtefactOutline[]
}

/** Walk up from `folderId`: does it reach the root without looping or passing through `guard`? */
function reachesRoot(byId: Map<string, Folder>, folderId: string | null, guard: string): boolean {
  const seen = new Set<string>([guard])
  let current = folderId
  while (current !== null) {
    if (seen.has(current)) return false
    seen.add(current)
    const node = byId.get(current)
    if (!node) return false
    current = node.parentFolderId
  }
  return true
}

const byPosition = (a: { position: number; folderId: string }, b: { position: number; folderId: string }): number =>
  a.position - b.position || a.folderId.localeCompare(b.folderId)

/** Assemble the flat server lists into the tree the panel renders. */
export function buildExplorerTree(folders: Folder[], artefacts: ArtefactOutline[]): ExplorerTree {
  const byId = new Map(folders.map((f) => [f.folderId, f]))
  const nodes = new Map<string, FolderNode>(
    folders.map((f) => [f.folderId, { folder: f, depth: 1, children: [], artefacts: [] }]),
  )

  const roots: FolderNode[] = []
  for (const folder of [...folders].sort(byPosition)) {
    const node = nodes.get(folder.folderId) as FolderNode
    // A dangling or cyclic parent degrades to the root — never to an invisible folder.
    const parentId =
      folder.parentFolderId !== null && reachesRoot(byId, folder.parentFolderId, folder.folderId)
        ? folder.parentFolderId
        : null
    const parent = parentId !== null ? nodes.get(parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  // Depth top-down, once the parent links are settled.
  const stack = roots.map((node) => ({ node, depth: 1 }))
  while (stack.length > 0) {
    const { node, depth } = stack.pop() as { node: FolderNode; depth: number }
    node.depth = depth
    for (const child of node.children) stack.push({ node: child, depth: depth + 1 })
  }

  const rootArtefacts: ArtefactOutline[] = []
  for (const artefact of artefacts) {
    const holder = artefact.folderId !== null ? nodes.get(artefact.folderId) : undefined
    if (holder) holder.artefacts.push(artefact)
    else rootArtefacts.push(artefact)
  }

  return { roots, artefacts: rootArtefacts }
}

/** The artefacts in the order the explorer lists them: each folder's subfolders then its own
 *  artefacts, depth first, and the root's artefacts last (LeftNav's render order). */
export function explorerOrder(tree: ExplorerTree): ArtefactOutline[] {
  const walk = (node: FolderNode): ArtefactOutline[] => [...node.children.flatMap(walk), ...node.artefacts]
  return [...tree.roots.flatMap(walk), ...tree.artefacts]
}

/** Can a folder at this depth still hold a subfolder? `maxDepth` is 2 in the template library. */
export function acceptsSubfolder(depth: number, maxDepth: number = MAX_FOLDER_DEPTH): boolean {
  return depth < maxDepth
}

/**
 * The assembled node for `folderId`, or null when the tree has no such folder.
 *
 * The panel keeps a *selected* folder — what the "new folder" / "new artefact" buttons create
 * into — as a bare id, so it survives a poll that rebuilds the tree. Resolving it here is also
 * how a selection pointing at a folder a teammate has just deleted degrades to the root rather
 * than to a dangling destination.
 */
export function findFolderNode(roots: FolderNode[], folderId: string): FolderNode | null {
  const stack = [...roots]
  while (stack.length > 0) {
    const node = stack.pop() as FolderNode
    if (node.folder.folderId === folderId) return node
    stack.push(...node.children)
  }
  return null
}

/**
 * Narrow the tree to what matches `query` (case-insensitive, on folder and artefact titles).
 *
 * A folder whose OWN title matches keeps its whole content — the user asked for that folder, so
 * showing it half-empty would read as "these are all the artefacts in it". Otherwise a folder
 * survives only as the path to a match, with its non-matching content dropped. An empty query
 * returns the tree untouched.
 */
export function filterExplorerTree(tree: ExplorerTree, query: string): ExplorerTree {
  const needle = query.trim().toLowerCase()
  if (needle === '') return tree
  const hit = (title: string): boolean => title.toLowerCase().includes(needle)

  const keep = (node: FolderNode): FolderNode | null => {
    if (hit(node.folder.title)) return node
    const children = node.children.map(keep).filter((n): n is FolderNode => n !== null)
    const artefacts = node.artefacts.filter((a) => hit(a.title))
    if (children.length === 0 && artefacts.length === 0) return null
    return { ...node, children, artefacts }
  }

  return {
    roots: tree.roots.map(keep).filter((n): n is FolderNode => n !== null),
    artefacts: tree.artefacts.filter((a) => hit(a.title)),
  }
}

/** Ordered ids of the folders sharing `parentFolderId` (null = root) — mirrors the backend's
 *  `childFolders`/`sortFolders` (position, then id as a tie-break). */
export function siblingFolderIds(folders: Folder[], parentFolderId: string | null): string[] {
  return folders
    .filter((f) => f.parentFolderId === parentFolderId)
    .sort(byPosition)
    .map((f) => f.folderId)
}

/**
 * The zone of a folder row a drag is hovering, from the pointer's offset into the row and the
 * row's height: the top and bottom slivers ask to become a SIBLING there — reordering within (or
 * moving to a precise slot of) the row's own parent — while the middle asks to nest INSIDE it. The
 * two are drawn with the same `drop-before`/`drop-after` vs `drop-into` indicators the rest of the
 * tree already uses.
 *
 * A zero-height row (untouched `getBoundingClientRect` in a test with no layout) always reads as
 * `nest` — the pre-existing behaviour dropping a folder onto another one always had.
 */
export type FolderDropZone = 'before' | 'after' | 'nest'
export function folderDropZone(offsetY: number, height: number): FolderDropZone {
  if (height <= 0) return 'nest'
  const ratio = offsetY / height
  if (ratio < 0.25) return 'before'
  if (ratio > 0.75) return 'after'
  return 'nest'
}

/**
 * The `index` to hand `PATCH /v1/artefacts/move` so a dropped artefact lands where the indicator
 * promised: ABOVE the row it was dropped on (`.item.drop-before` draws that line on the target's
 * top border).
 *
 * The two sides count in different spaces. The panel numbers the rows it renders, the dragged
 * artefact included; the server ranks the destination's siblings AFTER lifting that artefact out
 * (`placeAt`, backend/domain/folders.ts). So dragging DOWNWARD inside one folder shifts every slot
 * below the artefact's old row up by one, and the raw row index would drop it one slot too low —
 * under the row the user aimed above. Dragging upward, and any drop into another folder (where the
 * artefact is absent from the list, so nothing shifts), need no correction.
 */
export function dropIndexBefore(renderedIds: string[], movedId: string, targetIndex: number): number {
  const from = renderedIds.indexOf(movedId)
  return from !== -1 && from < targetIndex ? targetIndex - 1 : targetIndex
}

/** How many levels the subtree rooted at `folderId` occupies (1 = no subfolder). Cycle-safe. */
export function subtreeHeight(folders: Folder[], folderId: string): number {
  const seen = new Set<string>([folderId])
  let level = [folderId]
  let height = 0
  while (level.length > 0) {
    height++
    const next: string[] = []
    for (const id of level) {
      for (const child of folders) {
        if (child.parentFolderId !== id || seen.has(child.folderId)) continue
        seen.add(child.folderId)
        next.push(child.folderId)
      }
    }
    level = next
  }
  return height
}

/**
 * Is dropping `folderId` into `targetId` (null = root) worth offering? Mirrors the backend's
 * `canNestFolder`: no move into itself or its own subtree, and the WHOLE moved subtree has to fit
 * under the 5-level limit (`maxDepth`: 2 in the template library). The server re-validates — this
 * only keeps the UI from inviting a 422.
 */
export function canDropFolder(
  folders: Folder[],
  folderId: string,
  targetId: string | null,
  targetDepth: number,
  maxDepth: number = MAX_FOLDER_DEPTH,
): boolean {
  if (targetId === folderId) return false
  if (targetId === null) return true // the root always fits
  const byId = new Map(folders.map((f) => [f.folderId, f]))
  // Walking up from the target must not run into the moved folder (that would be its own subtree).
  let current: string | null = targetId
  const seen = new Set<string>()
  while (current !== null) {
    if (current === folderId || seen.has(current)) return false
    seen.add(current)
    current = byId.get(current)?.parentFolderId ?? null
  }
  return targetDepth + subtreeHeight(folders, folderId) <= maxDepth
}
